// Metroid Prime: Origins snapshot report: HTML for a slot save's stats (see extractSlotStats), in
// the shared game_tool tabs. The Edit tab's field editor is live DOM the controller builds once;
// this only leaves a slot for it (data-mpo-edit-slot).
import { esc, img } from "./game_tool/html"
import { fraction, percent } from "./game_tool/format"
import { SHOW_ALL_TAB, tabSet, withShowAll } from "./game_tool/tabs"
import {
  activeFilter, bar, COLLECTION_FILTERS, completionEntry, completionRow, completionSectionButtons, count, emptyState,
  filterEmptyText, filterGroup, itemCard, ownedKey, ownedTag, panel, tallyPanel, toggle,
} from "./game_tool/components"
import { AREAS } from "./metroid_prime_origins_slot_stats"
import { staticImageUrl } from "./static_images"

const PREFIX = "mpo"
const CREATURES = "creatures"

const MAIN_TABS = [
  ["overview", "Overview"],
  ["collection", "Collection"],
  ["aeon", "Aeon Powers"],
  ["logbook", "Logbook"],
  ["creatures", "Creatures"],
  ["edit", "Edit"],
]

// --- Sprites (extracted from the game) --------------------------------------------

const IMG = staticImageUrl("/tools/metroid-prime-origins/img")
const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")

// spr_creatures frames, named for their entry; frame 0 is the unscanned "?".
const creatureSprite = (name) => `${IMG}/creatures/${slug(name)}.png`
const UNKNOWN_CREATURE_SPRITE = `${IMG}/creatures/unknown.png`
// sprChozoArtifacts frames (frame n - 1 is Artifact n).
const artifactSprite = (name) => `${IMG}/artifacts/${slug(name)}.png`
// The in-world pickup (sprItem*_strip3, first frame) for each collection.
const PICKUP_SPRITES = {
  missiles: `${IMG}/pickups/missile.png`,
  "energy-tanks": `${IMG}/pickups/energy-tank.png`,
  "power-bombs": `${IMG}/pickups/power-bomb.png`,
}
// sprItem* first frames, named for the equipment label. Starting gear has no pickup sprite.
const STARTING_EQUIPMENT = new Set(["Power Suit", "Power Beam"])
const equipmentSprite = (label) => (STARTING_EQUIPMENT.has(label) ? "" : `${IMG}/equipment/${slug(label)}.png`)

export const EDIT_TAB = "edit"

/** Aeon Powers only for saves that track them; no Edit tab for the empty save (there's no file). */
function mainTabs(stats) {
  return MAIN_TABS.filter(([id]) => (id === "aeon" ? Boolean(stats.aeon) : id === EDIT_TAB ? !stats.empty : true))
}

export function tabIds(stats) {
  return [SHOW_ALL_TAB, ...mainTabs(stats).map(([id]) => id)]
}

// --- Overview -------------------------------------------------------------------

function metaItem(label, value, modifier = "") {
  return `<div class="game-tool__stats-meta-item${modifier ? ` game-tool__stats-meta-item--${modifier}` : ""}">` +
    `<dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>`
}

function renderMeta(stats) {
  const capacities = stats.capacities
    ? [
        metaItem("Health", fraction(stats.capacities.health.current, stats.capacities.health.max)),
        metaItem("Missiles", fraction(stats.capacities.missiles.current, stats.capacities.missiles.max)),
        metaItem("Power Bombs", fraction(stats.capacities.powerBombs.current, stats.capacities.powerBombs.max)),
      ]
    : []
  return `<dl class="game-tool__stats-meta">${[
    metaItem("Mode", stats.gameMode),
    metaItem("Playtime", stats.playtime),
    metaItem("Deaths", stats.deaths),
    ...capacities,
    metaItem("Save location", stats.location, "location"),
  ].join("")}</dl>`
}

function equipChip(item, lineStart) {
  return `<span class="game-tool__stats-equip-chip ${item.collected ? "is-collected" : "is-missing"}${lineStart ? " is-line-start" : ""}" role="listitem" ` +
    `aria-label="${esc(`${item.collected ? "Collected" : "Missing"}: ${item.label}`)}">` +
    `${img(equipmentSprite(item.label), "", "game-tool__stats-equip-icon") || '<span class="game-tool__stats-equip-icon" aria-hidden="true"></span>'}` +
    `<span class="game-tool__stats-equip-name">${esc(item.label)}</span></span>`
}

/**
 * One section as a grid of item / gap / item … columns, so every row's items line up. Gaps in
 * chained rows hold an arrow (beam -> combo, upgrade progressions); other gaps stay empty.
 */
function equipmentSection(section) {
  const columns = Math.max(...section.lines.map((line) => line.items.length * 2 - 1))
  const gap = (chained) => `<span class="game-tool__stats-equip-arrow" aria-hidden="true">${chained ? "→" : ""}</span>`
  const cells = section.lines.map((line) =>
    line.items.map((item, i) => `${i > 0 ? gap(line.chained) : ""}${equipChip(item, i === 0)}`).join("")).join("")
  return `<section class="game-tool__stats-box game-tool__stats-equipment-section--${section.id}">` +
    `<h3 class="game-tool__stats-collection-label">${esc(section.label)} ${count(fraction(section.collected, section.total))}</h3>` +
    bar(section.collected, section.total, section.label) +
    `<div class="game-tool__stats-equip-grid" style="--equip-columns: ${columns}" role="list" aria-label="${esc(section.label)}">${cells}</div></section>`
}

function runStatGroup(group) {
  const rows = group.stats.map((s) => `<div><dt>${esc(s.label)}</dt><dd>${esc(s.value)}</dd></div>`).join("")
  return `<section class="game-tool__stats-box"><h3 class="game-tool__stats-collection-label">${esc(group.label)}</h3>` +
    `<dl class="game-tool__stats-list">${rows}</dl></section>`
}

function renderOverview(stats) {
  const equipment = stats.equipment?.length
    ? panel("Equipment", `<div class="game-tool__stats-equipment">${stats.equipment.map(equipmentSection).join("")}</div>`)
    : ""
  // Run stats fold out inside the Save panel, closed by default.
  const runStats = stats.runStats?.length
    ? `<details class="game-tool__stats-details"><summary>Run stats</summary>` +
      `<div class="game-tool__stats-run">${stats.runStats.map(runStatGroup).join("")}</div></details>`
    : ""
  return panel("Save", renderMeta(stats) + runStats) + equipment
}

// --- Filters ----------------------------------------------------------------------

// Missing pickups only show "???" without spoilers, so listing them gives nothing away.
const COMPLETION_FILTERS = [
  { id: "all", label: "All" },
  { id: "missing", label: "Missing", icon: "fa-circle-exclamation" },
]
const CARD_FILTERS = COLLECTION_FILTERS.filter((f) => f.id !== "equipped")

const filterFor = (ui, key, filters) => activeFilter(filters, ui.filters?.[key], ui.spoilers).id

const filters = (ui, key, noun, list) =>
  filterGroup({ prefix: PREFIX, key, noun, filters: list, current: ui.filters?.[key], spoilers: ui.spoilers })

const FILTER_KEEP = {
  all: () => true,
  equipped: (item) => item.equipped,
  found: (item) => item.collected,
  missing: (item) => !item.collected,
}

/** Items the card filter `key` keeps. */
function filteredItems(items, ui, key, list = CARD_FILTERS) {
  return items.filter(FILTER_KEEP[filterFor(ui, key, list)])
}

const emptyText = (ui, key, noun, list = CARD_FILTERS) => filterEmptyText(filterFor(ui, key, list), noun)

// --- Aeon Powers -----------------------------------------------------------------

const AEON_SECTION = { id: "aeon", label: "Aeon Powers" }

function renderAeon(stats, ui, gameData) {
  const { aeon } = stats
  if (!aeon) return ""
  const texts = gameData?.aeon
  const described = isDescribed(ui, AEON_SECTION, texts)
  const cards = filteredItems(aeon.items, ui, "aeon", COLLECTION_FILTERS).map((item) => itemCard({
    name: item.label,
    showIcon: false,
    descriptionHtml: described && texts[item.label] ? logTextHtml(texts[item.label].text) : "",
    found: item.collected,
    equipped: item.equipped,
    revealed: ui.spoilers,
    noun: "Aeon Power",
    tagHtml: ownedTag(item.equipped),
  }))
  const equipped = `<span class="game-tool__muted">${aeon.equipped} equipped</span>`
  return tallyPanel("Aeon Powers", aeon.collected, aeon.total,
    ownedKey() + logGrid(cards, described, "", emptyText(ui, "aeon", "Aeon Powers", COLLECTION_FILTERS)),
    filters(ui, "aeon", "Aeon Powers", COLLECTION_FILTERS) + descriptionsToggle(ui, AEON_SECTION, texts) + equipped)
}

// --- Collection -------------------------------------------------------------------
// A completion tree like Castlevania's: each pickup collection, then its locations by area.
// Disclosures render open unless the player closed them (`ui.closed`, keyed by data-collapse).

function pickupLine(item, ui) {
  return completionEntry(item.collected, item.collected || ui.spoilers ? esc(item.room) : "???")
}

function areaRows(collection, ui, onlyMissing) {
  const areas = [...AREAS, ...new Set(collection.items.map((i) => i.area).filter((a) => !AREAS.includes(a)))]
  return areas.map((area) => {
    const items = collection.items.filter((i) => i.area === area)
    const shown = onlyMissing ? items.filter((i) => !i.collected) : items
    if (!shown.length) return ""
    const key = `mpo-${collection.id}-${slug(area || "other")}`
    return completionRow({
      label: area || "Other",
      done: items.filter((i) => i.collected).length,
      total: items.length,
      body: `<ul class="game-tool__completion-entries">${shown.map((item) => pickupLine(item, ui)).join("")}</ul>`,
      key,
      open: !ui.closed?.has(key),
    })
  }).join("")
}

function renderCollection(stats, ui) {
  if (!stats.collections.length) return ""
  const onlyMissing = filterFor(ui, "collection", COMPLETION_FILTERS) === "missing"
  const collected = stats.collections.reduce((n, c) => n + c.collected, 0)
  const total = stats.collections.reduce((n, c) => n + c.total, 0)
  const rows = stats.collections.map((c) => {
    const areas = areaRows(c, ui, onlyMissing)
    const key = `mpo-${c.id}`
    return completionRow({
      label: c.label,
      labelHtml: `${img(PICKUP_SPRITES[c.id], "", "game-tool__pickup-icon")}${esc(c.label)}`,
      done: c.collected,
      total: c.total,
      top: true,
      key,
      open: !ui.closed?.has(key),
      body: areas
        ? `<ul class="game-tool__completion-kinds">${areas}</ul>`
        : '<p class="game-tool__muted">Everything here is collected.</p>',
    })
  })
  return panel(`Collection ${count(`${fraction(collected, total)} · ${percent(collected, total)}%`)}`, `
    ${bar(collected, total, "Items collected")}
    <div class="game-tool__collection-controls">
      ${filters(ui, "collection", "pickups", COMPLETION_FILTERS)}
      ${completionSectionButtons()}
    </div>
    <ul class="game-tool__completion">${rows.join("")}</ul>`)
}

// --- Logbook cards (Creatures and Logbook tabs) -------------------------------------

/**
 * Logbook text as markup. The game's Scribble tags ([wave], [shake], [NES_<colour>]) are dropped
 * for now; gamedata.json keeps them.
 */
function logTextHtml(text) {
  return esc(text.replace(/\[\/?[A-Za-z_]+\]/g, "")).replace(/\n/g, "<br>")
}

const textBlock = (label, html) => `${label ? `<span class="game-tool__label">${esc(label)}</span>` : ""}${html}`

/** One logbook entry as an item card. `parts` are [label, html] blocks shown under the name. */
function logCard({ name, found, ui, parts = [], icon = "", lockedIcon = "", noun, tag = "Scanned" }) {
  return itemCard({
    name,
    icon,
    lockedIcon,
    showIcon: Boolean(icon),
    descriptionHtml: parts.map(([label, html]) => textBlock(label, html)).join("<br>"),
    found,
    revealed: ui.spoilers,
    noun,
    tagHtml: ownedTag(false, { label: tag }),
  })
}

function logGrid(cards, described, extraClass = "", emptyText = "") {
  if (!cards.length) return emptyState(emptyText)
  return `<ul class="game-tool__item-grid game-tool__item-grid--logbook${extraClass ? ` ${extraClass}` : ""}${described ? " is-described" : ""}">${cards.join("")}</ul>`
}

/** Each logbook section has its own Descriptions switch (data-setting="descriptions-<id>"). */
export const DESCRIBED_SECTIONS = ["aeon", CREATURES, "pirateData", "chozoLore", "artifacts"]

const isDescribed = (ui, section, texts) => Boolean(texts) && ui.descriptions?.[section.id] !== false

const descriptionsToggle = (ui, section, texts) =>
  (texts ? toggle("Descriptions", `descriptions-${section.id}`, isDescribed(ui, section, texts), { small: true, context: section.label }) : "")

const logbookSection = (stats, id) => stats.logbook?.sections?.find((s) => s.id === id)

function renderCreatures(stats, ui, gameData) {
  const section = logbookSection(stats, CREATURES)
  if (!section) return panel("Creatures", '<p class="game-tool__muted">No creature scans in this save.</p>')
  const described = isDescribed(ui, section, gameData?.creatures)
  const cards = filteredItems(section.items, ui, "creatures").map((item) => {
    const scan = described && gameData.creatures?.[item.label]
    return logCard({
      name: item.label,
      found: item.collected,
      ui,
      parts: scan ? [[scan.heading, logTextHtml(scan.text)]] : [],
      icon: creatureSprite(item.label),
      lockedIcon: UNKNOWN_CREATURE_SPRITE,
      noun: "creature",
    })
  })
  return tallyPanel("Creatures", section.collected, section.total,
    logGrid(cards, described, "game-tool__item-grid--creatures", emptyText(ui, "creatures", "creatures")),
    filters(ui, "creatures", "creatures", CARD_FILTERS) + descriptionsToggle(ui, section, gameData?.creatures))
}

// --- Logbook ---------------------------------------------------------------------

// Each list has its own All / Found / Missing filter, left of its Descriptions switch.
function sectionPanel(section, ui, texts, cardsHtml) {
  const controls = filters(ui, section.id, section.label, CARD_FILTERS) + descriptionsToggle(ui, section, texts)
  return panel(`${esc(section.label)} ${count(fraction(section.collected, section.total))}`,
    bar(section.collected, section.total, section.label) +
    (controls ? `<div class="game-tool__collection-controls">${controls}</div>` : "") + cardsHtml)
}

/** Pirate Data / Chozo Lore: title cards; `texts` is the gamedata section keyed by save name. */
function textSection(section, ui, texts, noun) {
  const described = isDescribed(ui, section, texts)
  const cards = filteredItems(section.items, ui, section.id).map((item) => {
    const entry = texts?.[item.label]
    return logCard({
      name: entry?.title || item.label,
      found: item.collected,
      ui,
      parts: described && entry ? [[null, logTextHtml(entry.text)]] : [],
      noun,
    })
  })
  return sectionPanel(section, ui, texts, logGrid(cards, described, "", emptyText(ui, section.id, section.label)))
}

function artifactSection(section, ui, hints) {
  const described = isDescribed(ui, section, hints)
  const cards = filteredItems(section.items, ui, section.id).map((item) => {
    const hint = described && hints[item.label]
    return logCard({
      name: `Artifact of ${item.label}`,
      found: item.collected,
      ui,
      parts: [...(hint ? [["Hint", logTextHtml(hint.text)]] : []), ["Location", esc(item.tip)]],
      icon: artifactSprite(item.label),
      noun: "artifact",
      tag: "Found",
    })
  })
  return sectionPanel(section, ui, hints, logGrid(cards, described, "game-tool__item-grid--artifacts", emptyText(ui, section.id, section.label)))
}

function renderLogbook(stats, ui, gameData) {
  if (!stats.logbook?.sections?.length) return ""
  const { collected, total, sections } = stats.logbook
  const summary = `<dl class="game-tool__stats-meta">${sections.map((s) => metaItem(s.label, fraction(s.collected, s.total))).join("")}</dl>`
  const parts = [
    panel(`Logbook ${count(`${fraction(collected, total)} · ${percent(collected, total)}%`)}`,
      `${bar(collected, total, "Logbook entries scanned")}${summary}`),
  ]
  const pirate = logbookSection(stats, "pirateData")
  const lore = logbookSection(stats, "chozoLore")
  const artifacts = logbookSection(stats, "artifacts")
  if (pirate) parts.push(textSection(pirate, ui, gameData?.pirateData, "log"))
  if (lore) parts.push(textSection(lore, ui, gameData?.chozoLore, "lore entry"))
  if (artifacts) parts.push(artifactSection(artifacts, ui, gameData?.artifacts))
  return parts.join("")
}

// --- Whole report -------------------------------------------------------------------

const RENDERERS = {
  overview: renderOverview,
  collection: renderCollection,
  aeon: renderAeon,
  creatures: renderCreatures,
  logbook: renderLogbook,
  edit: () => '<div data-mpo-edit-slot></div>',
}

/** The tabbed snapshot. Mutates `ui.tabs.main` to a valid tab. */
export function render(stats, ui, gameData = null) {
  if (!tabIds(stats).includes(ui.tabs.main)) ui.tabs.main = SHOW_ALL_TAB
  return `
    <div class="game-tool__toolbar">
      <button type="button" class="game-tool__btn game-tool__btn--ghost" data-load-another>Load another save</button>
      ${toggle("Show spoilers", "spoilers", ui.spoilers)}
    </div>
    <p class="game-tool__panel-note">Stats are from when this file was loaded, not live as you edit.</p>
    ${tabSet({
      prefix: PREFIX,
      group: "main",
      label: "Save sections",
      tabs: withShowAll(PREFIX, "main", mainTabs(stats).map(([id, label]) => [id, label, () => RENDERERS[id](stats, ui, gameData)])),
      selected: ui.tabs.main,
    })}`
}
